// Уровень «составной блок переполнен» — единственная копия условия: его зовут и
// хук редактора (useOverflowDetection), и замер скрытых холстов при публикации.
export const OVERFLOW_TOLERANCE_PX = 5
// Плеер ужимает блок до экрана (`fitArtboard`): до +20% высоты масштаб ~0,83 и
// текст ещё читается (`warn` — рекомендация), дальше он слишком мелкий (`over`).
export const OVERFLOW_ALLOWANCE = 0.2

export type DesignOverflowLevel = 'fits' | 'warn' | 'over'

export const designOverflowLevel = (canvas: HTMLElement): DesignOverflowLevel => {
  // Служебные части холста («+» внизу и примерка, `data-design-chrome`) ученику не
  // видны — в высоту контента их не засчитываем, иначе «переполнен» горел бы зря.
  const chrome = [...canvas.querySelectorAll<HTMLElement>('[data-design-chrome]')].reduce(
    (sum, node) => sum + node.offsetHeight,
    0,
  )
  // Нижний паддинг холста — тоже не контент: «переполнен» значит, что элемент
  // реально заходит за низ области над кнопкой «Далее», а не в поле отступа.
  const padBottom = parseFloat(getComputedStyle(canvas).paddingBottom) || 0
  const content = canvas.scrollHeight - chrome - padBottom
  if (content <= canvas.clientHeight + OVERFLOW_TOLERANCE_PX) return 'fits'
  return content > canvas.clientHeight * (1 + OVERFLOW_ALLOWANCE) ? 'over' : 'warn'
}

// В фоновой вкладке браузер приостанавливает rAF — без запасного таймера кадр
// не придёт никогда, и слот замера утёк бы навсегда.
const FRAME_FALLBACK_MS = 150

const nextFrame = (): Promise<void> =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, FRAME_FALLBACK_MS)
    requestAnimationFrame(() => {
      clearTimeout(timer)
      resolve()
    })
  })

// Ждём шрифты и картинки внутри root, но не дольше таймаута: битая картинка
// или зависший шрифт не должны вешать замер. Потом два кадра — раскладка осела.
// `timedOut` — вышли по таймауту, раскладка могла не осесть: такой замер не кешируют.
export const waitForLayoutReady = async (
  root: HTMLElement,
  timeoutMs = 10_000,
): Promise<{ timedOut: boolean }> => {
  const imagesReady = [...root.querySelectorAll<HTMLImageElement>('img')].map((img) =>
    img.complete
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true })
          img.addEventListener('error', () => resolve(), { once: true })
        }),
  )
  // Сначала заставляем браузер разложить новое поддерево: пока раскладки не было,
  // шрифт мог не начать грузиться, и `fonts.ready` уже разрешён — замер ушёл бы по запасным метрикам.
  void root.offsetHeight
  const ready = Promise.all([document.fonts?.ready, ...imagesReady])
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs)
  })
  const winner = await Promise.race([ready.then(() => 'ready' as const), timeout])
  clearTimeout(timer)
  for (let i = 0; i < 2; i++) await nextFrame()
  return { timedOut: winner === 'timeout' }
}

// Одновременно меряем не больше MEASURE_CONCURRENCY холстов — иначе курс с
// десятками составных блоков поднимет столько же скрытых холстов разом.
export const MEASURE_CONCURRENCY = 3

let activeSlots = 0
const waiters: Array<() => void> = []

const makeRelease = (): (() => void) => {
  let released = false
  return () => {
    if (released) return
    released = true
    const next = waiters.shift()
    if (next)
      next() // слот переходит следующему в очереди, счётчик не меняется
    else activeSlots -= 1
  }
}

export const acquireMeasureSlot = (): Promise<() => void> => {
  if (activeSlots < MEASURE_CONCURRENCY) {
    activeSlots += 1
    return Promise.resolve(makeRelease())
  }
  return new Promise((resolve) => {
    waiters.push(() => resolve(makeRelease()))
  })
}
